/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Sub-agent event plumbing shared across all AI chat surfaces.
 *
 * The backend emits nested events when a parent agent delegates to a
 * sub-agent via `AgentTool`. Each nested event arrives with this envelope:
 *
 *   {
 *     source: "sub_agent",
 *     parent_tool_call_id: string,   // parent agent's tool_call_id
 *     sub_agent_id: number,
 *     sub_agent_name: string,
 *     delegation_depth: number,
 *     type: "execution_started" | "tool_call" | "tool_result"
 *         | "llm_response" | "execution_completed" | "execution_error" | ...,
 *     ...original child event fields
 *   }
 *
 * The UI groups these events under the matching parent tool_call message
 * and renders them recursively: a nested `tool_call` event is itself a
 * container that can hold its own grandchildren. The helpers below are
 * used by both the main-assistant WebSocket hook and the agent-chat hook.
 */

/**
 * Return true when the websocket payload describes a nested sub-agent event.
 * @param {any} data
 * @returns {boolean}
 */
export function isSubAgentEvent(data) {
  return !!(data && data.source === 'sub_agent' && data.parent_tool_call_id);
}

/**
 * Find the tool_call node (top-level message or nested tool_call event)
 * whose identifier matches `toolCallId`. Iterates in reverse order so
 * that when multiple tool calls share the same id (e.g. provider omitted
 * ids and the backend assigned colliding fallbacks), the most recently
 * appended parent wins — which matches the streaming semantics where
 * nested events always belong to the currently running tool call.
 *
 * @param {Array<any>} nodes
 * @param {string} toolCallId
 * @returns {any|null}
 */
function findToolCallNode(nodes, toolCallId) {
  if (!Array.isArray(nodes) || !toolCallId) return null;
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i];
    if (!node || typeof node !== 'object') continue;
    const id = node.toolCallId || node.tool_call_id;
    const isToolCall =
      node.role === 'tool_call' ||
      node.role === 'TOOL_CALL' ||
      node.type === 'tool_call';
    if (isToolCall && id === toolCallId) {
      return node;
    }
    if (Array.isArray(node.subAgentEvents)) {
      const inner = findToolCallNode(node.subAgentEvents, toolCallId);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Given a list of messages, attach a sub-agent event to the matching
 * parent tool_call. Returns a new top-level array (new reference) so
 * React state updaters trigger a re-render.
 *
 * Rules:
 *   - Events are stored in order on the parent's `subAgentEvents` list.
 *   - A nested `tool_call` event becomes a container that can hold its
 *     own `subAgentEvents` for grandchildren.
 *   - A nested `tool_result` is either merged into the matching
 *     `tool_call` child (by `tool_call_id`) or appended as a standalone
 *     event when no matching child is found.
 *   - If no parent is found, the messages list is returned unchanged.
 *
 * @param {Array<any>} messages
 * @param {any} event
 * @returns {Array<any>}
 */
function attachEventToParent(parent, event) {
  const list = Array.isArray(parent.subAgentEvents)
    ? [...parent.subAgentEvents]
    : [];

  if (event.type === 'tool_result' && event.tool_call_id) {
    // Iterate in reverse so that when multiple tool_calls somehow share the
    // same tool_call_id (e.g. provider omitted ids and the backend assigned
    // colliding fallbacks) the most recently appended one — which the
    // backend is currently awaiting a result for — gets the result.
    let matchIndex = -1;
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i];
      if (e && e.type === 'tool_call' && e.tool_call_id === event.tool_call_id && e.result == null) {
        matchIndex = i;
        break;
      }
    }
    if (matchIndex < 0) {
      // Fall back to any match (already-completed ones) before giving up.
      matchIndex = list.findIndex(
        (e) => e && e.type === 'tool_call' && e.tool_call_id === event.tool_call_id
      );
    }
    if (matchIndex >= 0) {
      list[matchIndex] = {
        ...list[matchIndex],
        result: event.result,
        success: event.result && event.result.success,
      };
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '[subAgentEvents] tool_result has no matching tool_call sibling — appending standalone',
        {
          parent_tool_call_id: event.parent_tool_call_id,
          inner_tool_call_id: event.tool_call_id,
          tool_name: event.tool_name,
          known_children: list
            .filter((e) => e && e.type === 'tool_call')
            .map((e) => ({ id: e.tool_call_id, name: e.tool_name, done: e.result != null })),
        }
      );
      list.push(event);
    }
  } else {
    list.push(event);
  }

  parent.subAgentEvents = list;
}

/**
 * Given a list of messages, attach a sub-agent event to the matching
 * parent tool_call. Returns a new top-level array (new reference) so
 * React state updaters trigger a re-render.
 *
 * Rules:
 *   - Events are stored in order on the parent's `subAgentEvents` list.
 *   - A nested `tool_call` event becomes a container that can hold its
 *     own `subAgentEvents` for grandchildren.
 *   - A nested `tool_result` is either merged into the matching
 *     `tool_call` child (by `tool_call_id`) or appended as a standalone
 *     event when no matching child is found.
 *   - If no parent is found, the messages list is returned unchanged.
 *
 * @param {Array<any>} messages
 * @param {any} event
 * @returns {Array<any>}
 */
export function attachSubAgentEvent(messages, event) {
  if (!isSubAgentEvent(event)) return messages;
  if (!Array.isArray(messages)) return messages;

  const parent = findToolCallNode(messages, event.parent_tool_call_id);
  if (!parent) {
    // eslint-disable-next-line no-console
    console.warn(
      '[subAgentEvents] No parent tool_call found for sub-agent event',
      {
        parent_tool_call_id: event.parent_tool_call_id,
        event_type: event.type,
        sub_agent: event.sub_agent_name,
      }
    );
    return messages;
  }

  attachEventToParent(parent, event);
  return [...messages];
}

/**
 * Drain a pending-events buffer keyed by `parent_tool_call_id` into the
 * messages tree. Events whose parent is now present get attached;
 * events whose parent is still missing remain buffered.
 *
 * Mutates `pending` in place (deleting drained keys). Returns a new
 * top-level messages array when at least one event was attached,
 * otherwise returns the original array reference.
 *
 * @param {Array<any>} messages
 * @param {Record<string, Array<any>>} pending  in/out buffer
 * @returns {Array<any>}
 */
export function flushPendingSubAgentEvents(messages, pending) {
  if (!Array.isArray(messages) || !pending || typeof pending !== 'object') {
    return messages;
  }
  let didAttach = false;
  for (const parentId of Object.keys(pending)) {
    const parent = findToolCallNode(messages, parentId);
    if (!parent) continue;
    const queue = pending[parentId] || [];
    for (const ev of queue) {
      attachEventToParent(parent, ev);
      didAttach = true;
    }
    delete pending[parentId];
  }
  return didAttach ? [...messages] : messages;
}

function createStoppedToolResult() {
  return {
    success: false,
    data: { cancelled: true },
    error: 'Execution cancelled by user.',
  };
}

/**
 * Recursively marks nested tool calls as stopped and appends a synthetic
 * terminal cancellation event when a sub-agent started but never emitted a
 * terminal row before the parent execution stopped.
 *
 * @param {Array<any>} events
 * @returns {Array<any>}
 */
export function markSubAgentEventsStopped(events) {
  if (!Array.isArray(events) || events.length === 0) {
    return events;
  }

  let changed = false;
  const next = events.map((event) => {
    let updated = event;

    if (Array.isArray(event.subAgentEvents) && event.subAgentEvents.length > 0) {
      const nested = markSubAgentEventsStopped(event.subAgentEvents);
      if (nested !== event.subAgentEvents) {
        updated = { ...updated, subAgentEvents: nested };
        changed = true;
      }
    }

    if (event.type === 'tool_call' && (event.result === undefined || event.result === null)) {
      updated = {
        ...updated,
        result: createStoppedToolResult(),
        success: false,
      };
      changed = true;
    }

    return updated;
  });

  const startedEvent = [...next].reverse().find(
    (event) => event && event.type === 'execution_started'
  );
  const hasTerminalEvent = next.some(
    (event) => event && (event.type === 'execution_completed' || event.type === 'execution_error')
  );

  if (startedEvent && !hasTerminalEvent) {
    next.push({
      type: 'execution_completed',
      sub_agent_name: startedEvent.sub_agent_name,
      execution_id: startedEvent.execution_id,
      status: 'CANCELLED',
      message: 'Execution cancelled by user.',
    });
    changed = true;
  }

  return changed ? next : events;
}

export default attachSubAgentEvent;
